/**
 * Einen VORGANG auf einen anderen ziehen (PO 21.09.2026: „vorgänge kann man
 * per drag and drop in ein anderen vorgang schieben").
 *
 * Reine Ableitungen — DOM-frei, damit der node-only Vitest-Runner sie prüft.
 * Die Handhabung des `DataTransfer` bleibt in der Komponente.
 *
 * Eigener MIME-Typ statt `text/plain`, aus demselben Grund wie bei
 * `todoDrop.ts`: Nur so lässt sich beim ÜBERFAHREN entscheiden, ob die Zeile
 * den Zug annimmt. Der Browser gibt die Nutzlast erst beim Loslassen heraus
 * (`getData` ist während `dragover` leer) — die Typen aber immer.
 */

export const ZIEHTYP_VORGANG = "application/x-kmvs-vorgang";

/** Was mitreist: die Id für die Handlung, der Betreff für die Rückfrage. */
export interface VorgangZug {
    id: number;
    titel: string;
    /**
     * Trägt der Vorgang eigene To-dos? Entscheidet zusammen mit der Form des
     * Ziels, WAS beim Loslassen geschieht (`zugWeg`). Fehlt die Angabe, gilt
     * „keine To-dos" — so wie ein Vorgang ohne geladene Kinder aussieht.
     */
    hatTodos?: boolean;
}

/** Trägt dieser Zug einen Vorgang? Beantwortbar schon beim `dragover`. */
export function istVorgangZug(typen: readonly string[] | DOMStringList | undefined): boolean {
    if (!typen) return false;
    for (let i = 0; i < typen.length; i++) {
        if (typen[i] === ZIEHTYP_VORGANG) return true;
    }
    return false;
}

/**
 * Darf dieser Vorgang auf jenen gezogen werden?
 *
 * Auf sich selbst nicht — das ergibt keine Handlung, und der Server lehnt es
 * ohnehin ab. Wichtiger ist, dass die eigene Zeile beim Ziehen gar nicht erst
 * aufleuchtet: Sonst sieht es aus, als ginge es, und der Fehler kommt erst
 * nach dem Loslassen.
 */
export function darfHierher(gezogen: number | null | undefined, ziel: number): boolean {
    return gezogen != null && gezogen !== ziel;
}

/**
 * Der Satz in der Rückfrage. Er nennt BEIDE Betreffs und sagt, welcher bleibt
 * — „A wird zu B" allein verrät nicht, dass A danach verschwindet.
 */
export function verschmelzFrage(quelle: string, ziel: string): string {
    const q = quelle.trim() || "(ohne Betreff)";
    const z = ziel.trim() || "(ohne Betreff)";
    return `„${q}“ in „${z}“ verschmelzen?`;
}

/**
 * Was dabei passiert, in der Sprache des Anwenders — nicht in der des
 * Datenmodells. „Landet im Papierkorb" statt „wird weich gelöscht", und der
 * Hinweis auf die Umkehrbarkeit gehört dazu: Er ist der Unterschied zwischen
 * einer Warnung und einer Drohung.
 */
export function verschmelzErklaerung(quelle: string, ziel: string): string {
    const q = quelle.trim() || "(ohne Betreff)";
    const z = ziel.trim() || "(ohne Betreff)";
    return (
        `Alles aus „${q}“ — To-dos, Notizen, Mails, Anrufe, Termine und Dokumente — ` +
        `steht danach in „${z}“. Der Betreff „${z}“ bleibt die Überschrift.\n\n` +
        `„${q}“ landet im Papierkorb. Von dort lässt er sich zurückholen — sein Inhalt ` +
        `kommt dabei aber nicht mit, der bleibt drüben.`
    );
}

/**
 * WAS BEIM LOSLASSEN GESCHIEHT (PO 28.09.2026).
 *
 * > „Verschieben Vorgang ohne To-dos in einen anderen Vorgang mit To-dos:
 * > aktuell passiert nix, landet im Papierkorb, darf nicht sein, erwartet
 * > ist, dass er als To-do im anderen landet."
 *
 * Bis dahin verschmolz jeder Zug flach. Bei einem gegliederten Ziel kam der
 * Schriftwechsel der schlanken Quelle dort frei an, und der Server zog ihn in
 * das To-do mit dem Betreff des ZIELS — kein neues To-do, der Betreff der
 * Quelle verschwunden, die Quelle im Papierkorb.
 *
 * Die Tabelle dafür gibt es seit dem 22.09.2026 (`zusammenfuehrWege.ts`):
 * Hat der SIEGER To-dos und der Verlierer keine, ist „als To-do übernehmen"
 * der einzige Weg. Der Zug hält sich jetzt an dieselbe Regel — der Server
 * setzt sie zusätzlich selbst durch.
 */
export type ZugWeg = "alsTodo" | "verschmelzen";

export function zugWeg(quelle: Pick<VorgangZug, "hatTodos">, ziel: Pick<VorgangZug, "hatTodos">): ZugWeg {
    return !quelle.hatTodos && ziel.hatTodos ? "alsTodo" : "verschmelzen";
}

/** Die Rückfrage für den Weg „als To-do" — sie nennt, was daraus wird. */
export function alsTodoFrage(quelle: string, ziel: string): string {
    const q = quelle.trim() || "(ohne Betreff)";
    const z = ziel.trim() || "(ohne Betreff)";
    return `„${q}“ als To-do in „${z}“ übernehmen?`;
}

/** Was dabei passiert — Gegenstück zu `verschmelzErklaerung`. */
export function alsTodoErklaerung(quelle: string, ziel: string): string {
    const q = quelle.trim() || "(ohne Betreff)";
    const z = ziel.trim() || "(ohne Betreff)";
    return (
        `„${z}“ ist in To-dos gegliedert. „${q}“ wird dort ein neues To-do mit dem ` +
        `Betreff „${q}“ — samt Notizen, Mails, Anrufen, Terminen und Dokumenten.\n\n` +
        `Der leere Vorgang „${q}“ landet im Papierkorb. Von dort lässt er sich ` +
        `zurückholen — sein Inhalt kommt dabei aber nicht mit, der steht dann im To-do.`
    );
}

/**
 * DIE RICHTUNG UMKEHREN (T-246, 12.10.2026: „ich muss auswählen können,
 * welcher Vorgang führend ist").
 *
 * Beim Ziehen stand fest: Der getroffene Vorgang bleibt. Wer den falschen
 * gezogen hatte, musste abbrechen und andersherum ziehen — in einer langen
 * Liste oft unmöglich, weil die andere Zeile nicht zu sehen ist. Die Rückfrage
 * bietet deshalb „Umkehren" an: Quelle und Ziel tauschen, und ALLES Weitere
 * (Weg, Titel, Erklärung, Knopf) leitet sich neu aus dem getauschten Paar ab.
 *
 * Ein Sonderfall ist dafür nicht nötig: `zugWeg` ergibt „als To-do" nur für
 * eine Quelle OHNE To-dos. Auch nach dem Tausch kann also nie ein
 * gegliederter Vorgang „als To-do" verschoben werden (das lehnte der Server ab).
 */
export function tauscheZug<T extends { quelle: VorgangZug; ziel: VorgangZug }>(paar: T): T {
    return { ...paar, quelle: paar.ziel, ziel: paar.quelle };
}
